import React, { useEffect, useRef, useState, Fragment } from 'react';
import { Spin } from 'antd';

// Components
import * as S from './Styled';
import Icon from '../Icon';
import Spinner from '../Spinner';
import { ScrollWrapper } from '../../hoc';
import useOnClickOutside from '../../hooks/useOnClickOutside';

// Store
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Utils
import cn from 'classnames';
import { useHistory } from 'react-router-dom';
import api from '../../api';
import { setVhProperty } from '../../utils';
import { useDebounce } from 'react-use';
import { useLang } from '../../context/lang';
import useMobileSearchModal from '../../hooks/useMobileSearchModal';

const Search = ({
  label,
  placeholder,
  onChangeVisible,
  onCloseSearch,
  modular,
  opened,
  searchIsFoldable,
  ...props
}) => {
  const history = useHistory();
  const [open, setOpen] = useState(opened);
  const [inputValue, setInputValue] = useState('');
  const [items, setItems] = useState({ goods: [], goods_atc_classes: [] });
  const [total, setTotal] = useState(0);
  const [goodsIsShown, setGoodsIsShown] = useState(false);
  const [atxIsShown, setAtxIsShown] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation();
  const userLocation = useSelector(state => state.user.location);
  const searchRef = useRef(null);
  const textInputRef = useRef(null);
  const isMobileModal = useMobileSearchModal();
  const [focus, setFocus] = useState(false);
  const { language } = useLang();

  const [isFolded, setIsFolded] = useState(searchIsFoldable ?? false);

  useEffect(() => {
    setVhProperty();
    window.addEventListener('resize', setVhProperty);
    return () => {
      window.removeEventListener('resize', setVhProperty);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (open) {
      if (isMobileModal) {
        if (textInputRef.current) {
          textInputRef.current.focus();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useDebounce(
    async () => {
      try {
        if (inputValue.length === 0 || inputValue.length <= 2) {
          setTotal(0);
          setIsLoading(false);
          setItems({ goods: [], goods_atc_classes: [] });
          setIsLoading(false);
          return;
        }

        setIsLoading(true);
        const { data } = await api.get('search', {
          params: {
            latitude: userLocation.latitude,
            longitude: userLocation.longitude,
            query: inputValue,
          },
        });
        setTotal(data.total);
        setItems({
          goods: data.goods,
          goods_atc_classes: data.goods_atc_classes,
        });
        setOpen(true);
        setIsLoading(false);
      } catch (e) {
        console.error(e);
        setIsLoading(false);
      }
    },
    500,
    [inputValue]
  );

  const onChange = ({ target: { value } }) => {
    setInputValue(value);
  };

  const onClear = event => {
    event.preventDefault();
    setInputValue('');
  };

  const onSubmit = event => {
    event.preventDefault();
    if (inputValue?.length > 2) {
      history.push({
        pathname: `/catalog/${inputValue}`,
      });
      setInputValue('');
      onChangeVisible(false);
      onCloseSearch();
      setOpen(false);
      setFocus(false);
      setIsFolded(true);
    }
  };

  const onOpen = openState => {
    setOpen(openState);
    if (onChangeVisible) {
      onChangeVisible(openState);
    }
  };

  const onKeyDown = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      setInputValue('');
      onSubmit(event);
      setOpen(false);
    }
  };

  const onClose = event => {
    event.preventDefault();
    setFocus(false);
    setOpen(false);
    onChangeVisible(false);
    onCloseSearch();
  };

  const onFocus = () => {
    setFocus(true);
    setOpen(true);
    onChangeVisible(true);
  };

  const toggle = name => {
    if (name === 'goods') {
      setGoodsIsShown(true);
    }

    if (name === 'atx') {
      setAtxIsShown(true);
    }
  };

  const onClick = () => {
    setOpen(false);
    setInputValue('');
    setFocus(false);
    setItems({ goods: [], goods_atc_classes: [] });
    setTotal(0);
    onChangeVisible(false);
    onCloseSearch();
    setIsFolded(true);
  };

  const renderGoods = (items, isShown) => {
    if (items.length === 0) {
      return;
    }

    const list = isShown ? items : items.slice(0, 4);
    return (
      <S.Group>
        <S.Items>
          {list.map(listItem => {
            const {
              id,
              release_form,
              goods_producer,
              drugstore_price,
              online_price,
              pic,
            } = listItem;
            const itemName = listItem[`name_${language}`];

            return (
              <Fragment key={id}>
                <S.PreviewItem
                  onClick={onClick}
                  to={{
                    pathname: `/catalog/${inputValue}`,
                    state: {
                      modalId: id,
                    },
                  }}
                >
                  <S.GoodsData>
                    <S.Photo>
                      <S.Img
                        src={
                          pic.length
                            ? `${pic[0]}?w=40&h=40&q=90`
                            : '/assets/images/no_photo.jpg'
                        }
                        alt={pic.length ? itemName : t('labels.no_photo')}
                      />
                    </S.Photo>
                    <S.Content>
                      <S.Header>
                        <S.Title>{itemName}</S.Title>
                      </S.Header>
                      <S.Descr>
                        {release_form && <S.Tags>{release_form}</S.Tags>}
                        <S.Tags>
                          <S.TagsInner>
                            {goods_producer.country} &nbsp;-&nbsp;
                            <S.Txt>{goods_producer.name}</S.Txt>
                          </S.TagsInner>
                        </S.Tags>
                      </S.Descr>
                    </S.Content>
                  </S.GoodsData>
                  <S.Price>
                    {online_price > 0
                      ? `${online_price.toFixed(2)} - ${drugstore_price.toFixed(
                          2
                        )} ₴`
                      : null}{' '}
                  </S.Price>
                </S.PreviewItem>
              </Fragment>
            );
          })}
        </S.Items>

        {items.length > 4 && !isShown && (
          <S.Show onClick={() => toggle('goods')} className="search__link">
            {t('labels.show')} {t('labels.more')} {items.length - 4}
          </S.Show>
        )}
      </S.Group>
    );
  };

  const renderCustom = (items, isShown) => {
    if (items.length === 0) {
      return;
    }

    const list = isShown ? items : items.slice(0, 4);
    return (
      <>
        <S.GroupName>{t('labels.atx_classifier')}</S.GroupName>
        <S.Group>
          <S.CustomItems>
            {list.map(({ id, name }) => (
              <S.Item onClick={onClick} to={`/catalog/${name}`} key={id}>
                <S.GoodsData>{name}</S.GoodsData>
              </S.Item>
            ))}

            {items.length > 4 && !isShown && (
              <S.Show onClick={() => toggle('atx')} className="search__link">
                {t('labels.show')} {t('labels.more')} {items.length - 4}{' '}
                {t('labels.products')}
              </S.Show>
            )}
          </S.CustomItems>
        </S.Group>
      </>
    );
  };

  const dropdown = (
    <S.Dropdown open={open} modular={modular}>
      <ScrollWrapper>
        {isLoading ? (
          <Spinner margin={25} className="dropdown-spinner" />
        ) : (
          <>
            {items.goods.length ? (
              <S.Results>
                {renderGoods(items.goods, goodsIsShown)}
                {items.goods_atc_classes.length ? (
                  <>
                    <S.Div />
                    {renderCustom(items.goods_atc_classes, atxIsShown)}
                  </>
                ) : null}
              </S.Results>
            ) : (
              <S.NoResults className="no-results">
                {t('labels.nothing_was_found_for_your_search')}
              </S.NoResults>
            )}
            {items.goods.length > 0 && (
              <S.FooterBtn>
                <S.MenuSubmit type="submit">
                  {t('labels.show')}{' '}
                  {t('labels.products', {
                    count: total,
                  })}
                </S.MenuSubmit>
              </S.FooterBtn>
            )}
          </>
        )}
      </ScrollWrapper>
    </S.Dropdown>
  );

  useOnClickOutside(searchRef, () => {
    if (isMobileModal) {
      return;
    }

    setOpen(false);
    onChangeVisible(false);
    onCloseSearch();
    setFocus(false);
  });

  const onShowSearch = () => {
    setIsFolded(isFolded => !isFolded);
  };

  return (
    <>
      {searchIsFoldable ? (
        <S.ShowTrigger onClick={onShowSearch}>
          <Icon name="search_tr" />
        </S.ShowTrigger>
      ) : null}
      <S.ExtWrapper
        ref={searchRef}
        className={cn('search', {
          'is-active': open,
          'is-focus': !modular && focus,
          'is-folded': isFolded,
          'is-top-offset': searchIsFoldable,
        })}
        onSubmit={onSubmit}
      >
        <S.Controls>
          {open ? (
            <S.Close onClick={onClose}>
              <Icon name="close" size="small" />
            </S.Close>
          ) : null}
          <S.Inner>
            <S.InputIcon name="search" />
            <S.Input
              ref={textInputRef}
              placeholder={placeholder}
              value={inputValue}
              onChange={onChange}
              autoComplete="off"
              onKeyDown={onKeyDown}
              onClick={() => {
                if (isMobileModal) {
                  if (!open) {
                    onOpen(true);
                  }
                } else {
                  onFocus();
                }
              }}
              {...props}
              autoFocus={open && isMobileModal}
            />
            <S.SubmitBtn type="search" htmlType="submit">
              {t('buttons.find')}
            </S.SubmitBtn>
            {isLoading && inputValue?.length > 2 ? (
              <S.SearchLoader><Spin size="small" /></S.SearchLoader>
            ) : (
              <S.Clear
                className={cn({
                  'empty-field': inputValue?.length === 0,
                })}
                type="button"
                onClick={onClear}
              >
                <Icon name="clear" />
              </S.Clear>
            )}
          </S.Inner>
        </S.Controls>
        {total ? dropdown : null}
        <S.Overlay />
      </S.ExtWrapper>
    </>
  );
};

export default Search;
